import { getBackendUrl } from '../config';

/** Remove tags HTML da mensagem (evita <img> sem Bearer duplicando a mídia em `InternalChatMessageMedia`). */
export function stripInternalChatMessageCaption(html) {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Monta URL absoluta para mídia do chat interno (ex.: /chat-media/...). */
export function resolveInternalChatMediaUrl(mediaPath) {
  if (!mediaPath || typeof mediaPath !== 'string') return '';
  const p = mediaPath.trim();
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  const base = (getBackendUrl() || '').replace(/\/$/, '');
  if (!base) return p.startsWith('/') ? p : `/${p}`;
  return `${base}${p.startsWith('/') ? p : `/${p}`}`;
}

/**
 * Path para `api.get()` com Bearer (ex.: /chat-media/arquivo.jpg).
 * Evita <img src> sem Authorization (401).
 */
export function resolveInternalChatMediaFetchPath(mediaPath) {
  if (!mediaPath || typeof mediaPath !== 'string') return '';
  const p = mediaPath.trim();
  if (p.startsWith('http://') || p.startsWith('https://')) {
    try {
      const u = new URL(p);
      return `${u.pathname}${u.search}`;
    } catch {
      return p;
    }
  }
  return p.startsWith('/') ? p : `/${p}`;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp)$/i;

/** Preview no compositor: arquivo local antes do envio. */
export function isInternalChatImageFile(file) {
  if (!file || typeof file !== 'object') return false;
  if (typeof file.type === 'string' && file.type.startsWith('image/')) return true;
  if (typeof file.name === 'string') return IMAGE_EXT.test(file.name);
  return false;
}
/** Vídeo sem .webm genérico — .webm é tratado abaixo (voz vs vídeo). */
const VIDEO_EXT = /\.(mp4|mov|avi|mkv)$/i;
const AUDIO_EXT = /\.(ogg|opus|mp3|wav|m4a|aac)$/i;

/** Tipo de pré-visualização para arquivo local (File) antes do envio. */
export function getInternalChatLocalFilePreviewKind(file) {
  if (!file || typeof file !== 'object') return 'file';
  const t = typeof file.type === 'string' ? file.type.toLowerCase() : '';
  const name = typeof file.name === 'string' ? file.name : '';
  if (t.startsWith('image/') || IMAGE_EXT.test(name)) return 'image';
  if (t.startsWith('audio/')) return 'audio';
  if (AUDIO_EXT.test(name)) return 'audio';
  if (t.startsWith('video/')) return 'video';
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(name)) return 'video';
  return 'file';
}

/**
 * Avatar do participante em URL absoluta (alinhado ao MessagesList / atendimento).
 */
export function resolveParticipantAvatarUrl(rawValue, companyId) {
  if (!rawValue || typeof rawValue !== 'string') return '';
  const v = rawValue.trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  const backendUrl = (getBackendUrl() || '').replace(/\/$/, '');
  if (!backendUrl) return v.startsWith('/') ? v : `/${v}`;
  if (v.startsWith('/')) return `${backendUrl}${v}`;
  if (v.startsWith('public/')) return `${backendUrl}/${v}`;
  if (v.includes('/')) return `${backendUrl}/public/${v}`;
  const cid = companyId != null && companyId !== '' ? companyId : '';
  return `${backendUrl}/public/company${cid}/user/${v}`;
}

/** Inferência por extensão (backend não envia mimetype em ChatMessage). */
export function getInternalChatMediaKind(mediaPath, mediaName) {
  const n = `${mediaName || ''} ${mediaPath || ''}`.toLowerCase();
  if (IMAGE_EXT.test(n)) return 'image';
  // WebM: gravador do chat interno usa audio-*.webm (opus); antes caía em "vídeo" e a bolha ficava vazia.
  if (/\.webm$/i.test(n)) {
    if (/audio|gravacao|gravação|recording|ptt|voice|mic/i.test(n)) return 'audio';
    // Mídia servida em /chat-media/ ou /internal-chat/ é nota de voz (nome opaco); evita <video controls> nativo.
    if (/\/chat-media\//i.test(n) || /(^|\/)chat-media\//i.test(n) || /\/internal-chat\//i.test(n))
      return 'audio';
    return 'video';
  }
  if (VIDEO_EXT.test(n)) return 'video';
  if (AUDIO_EXT.test(n)) return 'audio';
  return 'file';
}

/**
 * Texto para preview no modal de excluir mensagem: legenda em texto quando existir;
 * só mídia (sem texto) → rótulo genérico por tipo (evita nomes técnicos tipo `1776…_chat9_.webm`).
 *
 * @param {{ message?: string, mediaPath?: string, mediaUrl?: string, mediaName?: string }} msg
 * @param {{ audioLabel: string, imageLabel: string, videoLabel: string, documentLabel: string }} labels
 * @returns {string}
 */
export function getInternalChatDeleteMessagePreview(msg, labels) {
  if (!msg || typeof msg !== 'object') return '';
  const caption = stripInternalChatMessageCaption(msg.message || '').trim();
  if (caption) return caption;
  const rawMedia = msg.mediaPath || msg.mediaUrl;
  if (!rawMedia && !msg.mediaName) return '';
  const kind = getInternalChatMediaKind(rawMedia, msg.mediaName);
  if (kind === 'image') return labels.imageLabel;
  if (kind === 'audio') return labels.audioLabel;
  if (kind === 'video') return labels.videoLabel;
  return labels.documentLabel;
}
